"use client";

import { useEffect, useState } from "react";
import "../marketing.css";

const outputDetails = {
  film: {
    label: "Memorial film",
    title: "A life, told in their voices.",
    description:
      "A cinematic, chaptered film that weaves photographs, real voice memories, video clips, and licensed music into one beautiful story.",
    meta: "16:9 live presentation · Offline rehearsal · MP4 master after picture lock",
  },
  poster: {
    label: "Signature poster",
    title: "The room-scale centerpiece.",
    description:
      "A 24 × 36 inch portrait-led poster with distance-view typography, a family-approved line, supporting photographs, and a measured-resolution print proof.",
    meta: "24 × 36 in · 300 DPI target · Bleed, trim, focal crop, and physical-proof gate",
  },
  program: {
    label: "Large service program",
    title: "Designed, proofed, and ready to print.",
    description:
      "A polished program with the service order, obituary, readings, photographs, and acknowledgements—created from the same approved story.",
    meta: "11 × 17 half-fold · 8.5 × 11 finished · Press preflight before export",
  },
  handout: {
    label: "Letter-size handout",
    title: "Familiar to hold. Effortless to print.",
    description:
      "A true four-panel memorial handout with the cover and thanks outside, and the service order and family-approved remembrance inside.",
    meta: "11 × 8.5 US Letter · Two-sided · One vertical fold · 5.5 × 8.5 finished",
  },
  story: {
    label: "Living tribute",
    title: "A beautiful place for every memory.",
    description:
      "A private, shareable story experience where family and friends can revisit the film, timeline, photographs, and original voices.",
    meta: "Private link · Mobile-first · No app required",
  },
  book: {
    label: "Keepsake book",
    title: "Something the family can hold onto.",
    description:
      "The strongest photographs, stories, and life chapters become a thoughtfully typeset hardcover book—not a raw export.",
    meta: "Hardcover · Gift copies · Print-ready PDF",
  },
} as const;

type OutputKey = keyof typeof outputDetails;

const productTour = {
  gather: {
    index: "01",
    label: "Gather",
    eyebrow: "One private link",
    title: "A contribution experience anyone can use.",
    description:
      "Friends and family can add a photograph, video, voice recording, or text memory from any phone. Every photograph asks for the story behind it, so the family receives meaning—not an unexplained camera roll.",
    bullets: [
      "No account, app, or password for contributors",
      "English and Spanish, with the original words preserved",
      "Name, relationship, and contact details captured once",
    ],
    image: "/images/product-contribute.webp",
    alt: "Gathered Spanish-language mobile contribution flow",
    frame: "mobile",
  },
  understand: {
    index: "02",
    label: "Understand",
    eyebrow: "Story intelligence",
    title: "See the whole life taking shape.",
    description:
      "Gathered connects people, dates, places, themes, and photographs across every contribution. It surfaces the strongest moments and gently shows the family where a chapter or voice may still be missing.",
    bullets: [
      "Source-linked facts and story excerpts",
      "Life chapters, themes, and thoughtful follow-up prompts",
      "Family review before a single word is published",
    ],
    image: "/images/product-intelligence.webp",
    alt: "Gathered Story Intelligence workspace showing a life timeline and themes",
    frame: "desktop",
  },
  create: {
    index: "03",
    label: "Create",
    eyebrow: "A private production studio",
    title: "Direct every detail without becoming an editor.",
    description:
      "Choose the emotional arc, approve photographs and words, set the pace, and proof every output in one calm workspace. AI does the organizing and first assembly; the family remains the final editor.",
    bullets: [
      "Cinematic chapters, licensed music, and graceful motion",
      "Swap, reorder, rewrite, or hold anything",
      "One approved story powers film, web, and print",
    ],
    image: "/images/product-production.webp",
    alt: "Gathered production studio with a cinematic timeline and family approvals",
    frame: "desktop",
  },
  share: {
    index: "04",
    label: "Share",
    eyebrow: "One cohesive tribute",
    title: "Ready for the room—and for the years after.",
    description:
      "Walk into the service with an offline presentation, a private memorial website, and print-ready materials that all feel like one beautifully art-directed story.",
    bullets: [
      "Offline video and presentation files",
      "Private, mobile-first memorial website",
      "Print-ready programs, posters, cards, and complete archive",
    ],
    image: "/images/product-memorial.webp",
    alt: "A finished Gathered memorial website with Eleanor's story and photographs",
    frame: "desktop",
  },
} as const;

type TourKey = keyof typeof productTour;

const appSignupUrl = "/login?mode=signup";
const appSignInUrl = "/login?mode=login";

function signupUrl(plan?: string) {
  return plan ? `${appSignupUrl}&plan=${encodeURIComponent(plan)}` : appSignupUrl;
}

const simpleFlow = [
  {
    index: "01",
    label: "Create & share",
    eyebrow: "One private invitation",
    title: "Create the space. Send one link anywhere.",
    description:
      "Start with her name, a photograph, and the service date if you have it. Gathered creates a private invitation you can text, email, or place in the family group chat—no contact list cleanup required.",
    proof: "Two minutes to create · No app for anyone",
  },
  {
    index: "02",
    label: "People contribute",
    eyebrow: "The photograph and the meaning",
    title: "Everyone adds a memory in the way that feels easiest.",
    description:
      "They choose a photograph or video, then tell the story by speaking or typing. Gathered keeps the contributor’s name, relationship, contact details, and original words attached to every memory.",
    proof: "Photo + story · Voice, text, audio, or video",
  },
  {
    index: "03",
    label: "Gathered organizes",
    eyebrow: "From scattered pieces to a whole life",
    title: "Every memory arrives organized and ready to shape.",
    description:
      "Gathered safely preserves the originals, connects related people and moments, spots duplicates and missing chapters, and prepares a thoughtful first cut. The family reviews everything before it becomes part of the tribute.",
    proof: "Source attached · Family approved · Full archive included",
  },
] as const;

const faqs = [
  {
    question: "What do I need to have before I start?",
    answer:
      "Just their name, the service date if you have one, and a few people to invite. You do not need to organize the photographs or write the life story first. Gathered guides everyone and shows you what is still missing.",
  },
  {
    question: "Do family and friends need an app or account?",
    answer:
      "No. They open one private link on any phone or computer, choose how they knew the person, and respond with a voice recording, text, photographs, or a short video. Most contributions take two or three minutes.",
  },
  {
    question: "Does AI invent or rewrite our memories?",
    answer:
      "Never. Gathered organizes and shapes only what your people provide. Important names, dates, and sensitive details are traced back to their source and require family approval before appearing in any output.",
  },
  {
    question: "Will the slideshow work without internet at the service?",
    answer:
      "Yes. Ceremony packages include downloadable, offline-ready video and presentation files, plus print-ready PDFs. You can test everything on the exact laptop, television, or projector before the room fills.",
  },
  {
    question: "How do you protect photographs and private memories?",
    answer:
      "Everything is private by default. Original uploads are preserved untouched, media is delivered through expiring access links, and only invited organizers can review contact information. Every family can download a complete archive at any time.",
  },
  {
    question: "Can Spanish-speaking family members contribute?",
    answer:
      "Yes. Contributors can use Gathered in English or Spanish and can speak rather than type. Their original words are always preserved, with an optional translation shown beside them for family review.",
  },
];

function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

function Check({ children }: { children: React.ReactNode }) {
  return (
    <span className="check-line">
      <span className="check-icon" aria-hidden="true">
        ✓
      </span>
      {children}
    </span>
  );
}

function FlowVisual({ step }: { step: number }) {
  if (step === 0) {
    return (
      <div className="flow-demo flow-demo-share" aria-hidden="true">
        <div className="flow-invite-card">
          <div className="flow-invite-head"><BrandMark /><span>Private invitation</span></div>
          <div className="flow-invite-person"><span>E</span><div><small>Help us remember</small><strong>Eleanor James</strong></div></div>
          <p>Share a photograph and the story behind it.</p>
          <div className="flow-link"><span>⌁</span><strong>gathered.link/eleanor</strong><em>Copied</em></div>
        </div>
        <div className="flow-send-label">Send it wherever people already are</div>
        <div className="flow-recipient-stack">
          <div><span>Messages</span><strong>James family</strong><small>8 people · Delivered</small></div>
          <div><span>Email</span><strong>Friends of Eleanor</strong><small>12 people · Sent</small></div>
          <div><span>Family chat</span><strong>One private link</strong><small>No sign-in required</small></div>
        </div>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div className="flow-demo flow-demo-contribute" aria-hidden="true">
        <div className="flow-contributor-phone">
          <div className="flow-phone-head"><BrandMark /><span>Remembering Eleanor</span><em>2 of 3</em></div>
          <div className="flow-memory-photo">
            <img src="/images/rose-book-club-v2.webp" alt="" />
            <span>Photo added</span>
          </div>
          <div className="flow-story-field">
            <small>The story behind this photograph</small>
            <p>“This was the summer she taught all of us to make peach pie…”</p>
            <div className="flow-voice-line"><i>●</i><span /><span /><span /><span /><span /><em>0:38</em></div>
          </div>
          <div className="flow-person-line"><span>MJ</span><div><strong>Maria Jensen</strong><small>College roommate · Email saved privately</small></div><em>✓</em></div>
        </div>
        <div className="flow-attached-note"><span>✓</span><div><small>Saved together</small><strong>Original + story + source</strong></div></div>
      </div>
    );
  }

  return (
    <div className="flow-demo flow-demo-organize" aria-hidden="true">
      <div className="flow-incoming">
        <small>Memories arriving</small>
        <div><span>MJ</span><strong>Peach pie summer</strong><em>Photo + voice</em></div>
        <div><span>CR</span><strong>The college road trip</strong><em>3 photos + story</em></div>
        <div><span>AK</span><strong>Her open-door table</strong><em>Video + text</em></div>
      </div>
      <div className="flow-organize-arrow"><span>✦</span><small>Gathered</small></div>
      <div className="flow-story-board">
        <div className="flow-board-head"><div><small>Eleanor’s tribute</small><strong>A whole life taking shape</strong></div><span>Family review</span></div>
        <div className="flow-board-stat"><strong>38</strong><span>memories from 14 people</span><em>0 mystery photos</em></div>
        <div className="flow-board-timeline">
          <span><i />Early years<small>7</small></span>
          <span><i />Family &amp; home<small>12</small></span>
          <span><i />Work &amp; impact<small>8</small></span>
          <span><i />Traditions &amp; joy<small>11</small></span>
        </div>
        <div className="flow-board-ready"><span>✓</span><div><small>First tribute draft</small><strong>Ready for the family to review</strong></div><em>92%</em></div>
      </div>
    </div>
  );
}

function TourPreview({ mode }: { mode: TourKey }) {
  if (mode === "gather") {
    return <div className="live-phone">
      <div className="live-phone__top"><BrandMark /><strong>Gathered</strong><span>EN · ES</span></div>
      <div className="live-phone__hero"><img src="/images/rose-portrait-v2.webp" alt="Eleanor smiling in a garden" /><div><small>REMEMBERING</small><strong>Eleanor James</strong><span>1951–2026</span></div></div>
      <div className="live-phone__steps"><b>1</b><i /><span>2</span><i /><span>3</span></div>
      <div className="live-phone__body"><small>A MEMORY FROM YOU</small><h4>Share a photograph and the story it carries.</h4><div className="live-upload"><img src="/images/rose-book-club-v2.webp" alt="Family photographs from Eleanor's life" /><div><strong>Sunday dinners at her house</strong><p>“There was always another chair at Eleanor’s table…”</p></div></div><button>Continue with this memory →</button></div>
    </div>;
  }

  if (mode === "understand") {
    return <div className="live-dashboard">
      <aside><BrandMark /><span>Overview</span><b>Memories</b><span>Story map</span><span>Invitees</span><span>Outputs</span></aside>
      <div className="live-dashboard__main"><header><div><small>ELEANOR’S STORY</small><strong>A whole life is taking shape.</strong></div><span>Family review</span></header><div className="live-metrics"><div><b>38</b><span>memories</span></div><div><b>14</b><span>voices</span></div><div><b>6</b><span>life chapters</span></div></div><div className="live-dashboard__grid"><section><small>STORY COVERAGE</small><h4>Two chapters need one more voice.</h4>{[["Early years",62],["Family & home",100],["Work & impact",88],["Traditions & joy",94]].map(([label,value])=><div className="live-coverage" key={label}><span>{label}</span><i><b style={{width:`${value}%`}} /></i><em>{value}%</em></div>)}</section><section className="live-theme"><small>THEME EMERGING</small><div className="live-theme__photos"><img src="/images/rose-portrait-v2.webp" alt="Eleanor" /><img src="/images/rose-book-club-v2.webp" alt="Eleanor's life archive" /></div><h4>“There was always room.”</h4><p>Appears across 11 memories from family, neighbors, and friends.</p></section></div></div>
    </div>;
  }

  if (mode === "create") {
    return <div className="live-editor"><header><div><BrandMark /><strong>Eleanor’s memorial film</strong></div><span>Saved · Private</span></header><div className="live-editor__workspace"><aside><small>CHAPTERS</small>{["Opening","Early years","Family & home","The open door","Legacy"].map((item,index)=><span className={index===3?"active":""} key={item}><i>{index+1}</i>{item}</span>)}</aside><main><div className="live-film-frame"><img src="/images/rose-book-club-v2.webp" alt="Eleanor surrounded by family photographs" /><div><small>CHAPTER FOUR</small><strong>The door was always open.</strong><span>— Claire, her daughter</span></div></div><div className="live-timeline"><div className="live-track live-track--photo"><span>PHOTOS</span><i /><i /><i /><i /></div><div className="live-track live-track--story"><span>STORY</span><b>Sunday dinners · Claire’s memory</b></div><div className="live-track live-track--music"><span>MUSIC</span><em>Licensed instrumental · 02:14</em></div><u /></div></main></div>
    </div>;
  }

  return <div className="live-memorial"><nav><BrandMark /><div><span>Her story</span><span>Memories</span><span>Timeline</span></div><b>Share a memory</b></nav><div className="live-memorial__hero"><img src="/images/rose-portrait-v2.webp" alt="Portrait of Eleanor" /><div><small>IN LOVING MEMORY</small><h4>Eleanor James</h4><p>1951–2026</p><blockquote>She made people feel expected, even when they arrived by surprise.</blockquote></div></div><div className="live-memorial__stories"><article><span>01</span><strong>The door was always open.</strong><small>11 memories · 18 photographs</small></article><article><span>02</span><strong>Joy was in the details.</strong><small>8 memories · 12 photographs</small></article><article><span>03</span><strong>What she taught us.</strong><small>9 voices · 3 recordings</small></article></div></div>;
}

export function MarketingHome() {
  const [activeOutput, setActiveOutput] = useState<OutputKey>("film");
  const [activeTour, setActiveTour] = useState<TourKey>("gather");
  const [activeFlow, setActiveFlow] = useState(0);
  const [flowPaused, setFlowPaused] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const active = outputDetails[activeOutput];
  const tour = productTour[activeTour];
  const flow = simpleFlow[activeFlow];

  useEffect(() => {
    if (flowPaused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const timer = window.setInterval(() => {
      setActiveFlow((current) => (current + 1) % simpleFlow.length);
    }, 5600);

    return () => window.clearInterval(timer);
  }, [flowPaused]);

  useEffect(() => {
    document.body.classList.add("marketing-active");

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const revealTargets = document.querySelectorAll<HTMLElement>(
      ".marketing-site > section:not(.hero), .marketing-site .process-row, .marketing-site .price-card, .marketing-site .output-card"
    );

    let observer: IntersectionObserver | undefined;
    if (!reduceMotion && "IntersectionObserver" in window) {
      document.body.classList.add("marketing-motion");
      revealTargets.forEach((element) => element.classList.add("marketing-reveal"));
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-visible");
            observer?.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -8%", threshold: 0.08 }
      );
      revealTargets.forEach((element) => observer?.observe(element));
    }

    return () => {
      observer?.disconnect();
      document.body.classList.remove("marketing-active", "marketing-motion");
    };
  }, []);

  return (
    <main className="marketing-site">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Gathered home">
          <BrandMark />
          <span>Gathered</span>
        </a>
        <nav aria-label="Main navigation">
          <a href="#simple-flow">How it works</a>
          <a href="#product-tour">Product</a>
          <a href="#pricing">Packages</a>
          <a href="#partners">For professionals</a>
          <a href={appSignInUrl}>Sign in</a>
        </nav>
        <a className="header-cta" href={appSignupUrl}>
          Create a memorial
        </a>
      </header>

      <section className="hero" id="top">
        <div className="hero-copy">
          <div className="eyebrow">
            <span className="eyebrow-dot" />
            Private memory collection + tribute production
          </div>
          <h1>You don’t have to carry this alone.</h1>
          <p className="hero-lede">
            Replace scattered texts, email attachments, and mystery photographs
            with one private link. Friends share the photograph and its story;
            Gathered keeps everything together and prepares a family-approved tribute.
          </p>
          <div className="hero-actions">
            <a className="button button-primary" href={appSignupUrl}>
              Create a memorial <span aria-hidden="true">→</span>
            </a>
            <a className="text-link" href="#simple-flow">
              See how simple it is
            </a>
          </div>
          <div className="assurance-row" aria-label="Product assurances">
            <Check>No app for contributors</Check>
            <Check>English + Spanish</Check>
            <Check>Private by default</Check>
          </div>
        </div>

        <div className="hero-visual" aria-label="A family gathering memories">
          <div className="hero-photo-wrap">
            <img
              className="hero-photo"
              src="/images/rose-family-memory-v2.webp"
              alt="A family member listening to a voice memory at a warm dining table"
              fetchPriority="high"
              decoding="async"
            />
            <div className="hero-photo-wash" />
            <div className="hero-caption">
              <span className="mini-avatar">E</span>
              <span>
                <small>Celebrating</small>
                Eleanor James · 1951–2026
              </span>
            </div>
          </div>
          <div className="floating-card memory-count">
            <div className="stacked-avatars" aria-hidden="true">
              <span>MJ</span>
              <span>CR</span>
              <span>AK</span>
            </div>
            <div>
              <strong>18 memories gathered</strong>
              <small>6 new today</small>
            </div>
          </div>
          <div className="floating-card story-ready">
            <div className="ready-ring">
              <span>84%</span>
            </div>
            <div>
              <small>Life story coverage</small>
              <strong>Nearly complete</strong>
            </div>
          </div>
          <div className="soft-orb orb-one" />
          <div className="soft-orb orb-two" />
        </div>
      </section>

      <section className="relief-band" aria-label="Simple product summary">
        <div>
          <span>01</span>
          <strong>Create one private space</strong>
        </div>
        <div className="band-arrow" aria-hidden="true">
          →
        </div>
        <div>
          <span>02</span>
          <strong>Everyone adds a little</strong>
        </div>
        <div className="band-arrow" aria-hidden="true">
          →
        </div>
        <div>
          <span>03</span>
          <strong>Everything comes together</strong>
        </div>
      </section>

      <section className="proof-strip" aria-label="Gathered product promises">
        <div><strong>1</strong><span>private link for everyone</span></div>
        <div><strong>100%</strong><span>family reviewed</span></div>
        <div><strong>2</strong><span>first-class languages</span></div>
        <div><strong>Every</strong><span>original included in your archive</span></div>
      </section>

      <section className="section simple-flow-section" id="simple-flow">
        <div className="section-kicker">A calmer way to gather a life</div>
        <div className="section-heading split-heading simple-flow-heading">
          <h2>A memorial shouldn’t begin with a scavenger hunt.</h2>
          <p>
            In the usual scramble, a photograph arrives in one text, its story
            comes days later in an email, and nobody remembers who sent the
            original. Gathered keeps the person, memory, and meaning together from the start.
          </p>
        </div>

        <div className="stress-contrast" aria-label="The usual collection process compared with Gathered">
          <article className="old-way-card">
            <div className="contrast-head"><span>The usual way</span><em>Scattered</em></div>
            <div className="chaos-canvas" aria-hidden="true">
              <div className="chaos-message message-one"><small>TEXT · AUNT SUSAN</small><strong>Here are a few I found</strong><span>IMG_4832.jpg</span></div>
              <div className="chaos-message message-two"><small>EMAIL · TOM</small><strong>memorial photos.zip</strong><span>12 attachments</span></div>
              <div className="chaos-message message-three"><small>FAMILY CHAT</small><strong>Who is standing next to Mom?</strong><span>Seen by 9</span></div>
              <div className="chaos-photo"><span>?</span><small>No name. No date.<br />No story.</small></div>
            </div>
            <div className="contrast-consequence">
              <span>14 conversations to chase</span>
              <span>Stories separated from photographs</span>
              <span>Duplicates, missing originals, unknown sources</span>
            </div>
          </article>

          <div className="contrast-shift" aria-hidden="true"><span>→</span><small>One link</small></div>

          <article className="gathered-way-card">
            <div className="contrast-head"><span>With Gathered</span><em>Ready</em></div>
            <div className="gathered-summary">
              <div className="summary-person"><span>E</span><div><small>Remembering</small><strong>Eleanor James</strong></div></div>
              <div className="summary-score"><strong>38</strong><span>complete memories</span></div>
              <div className="summary-grid">
                <span><strong>14</strong><small>contributors</small></span>
                <span><strong>38</strong><small>stories attached</small></span>
                <span><strong>0</strong><small>mystery photos</small></span>
              </div>
              <div className="summary-ready"><span>✓</span><div><small>Everything in one place</small><strong>Organized and ready to review</strong></div></div>
            </div>
            <div className="contrast-consequence gathered-consequence">
              <span>One private invitation</span>
              <span>Every photograph arrives with its story</span>
              <span>Original, contributor, and context stay connected</span>
            </div>
          </article>
        </div>

        <div className={`flow-player ${flowPaused ? "paused" : ""}`}>
          <div className="flow-player-intro">
            <div>
              <span>The Gathered flow</span>
              <h3>Three steps. No production experience required.</h3>
            </div>
            <button
              className="flow-play-control"
              type="button"
              onClick={() => setFlowPaused((paused) => !paused)}
              aria-label={flowPaused ? "Play the Gathered process" : "Pause the Gathered process"}
            >
              <span aria-hidden="true">{flowPaused ? "▶" : "Ⅱ"}</span>
              {flowPaused ? "Play" : "Pause"}
            </button>
          </div>

          <div className="flow-step-tabs" role="tablist" aria-label="How Gathered works">
            {simpleFlow.map((step, index) => (
              <button
                type="button"
                role="tab"
                aria-selected={activeFlow === index}
                className={activeFlow === index ? "active" : ""}
                key={step.index}
                onClick={() => {
                  setActiveFlow(index);
                  setFlowPaused(true);
                }}
              >
                <span>{step.index}</span>
                <strong>{step.label}</strong>
                <i aria-hidden="true" />
              </button>
            ))}
          </div>

          <div className="flow-slide" key={activeFlow} role="tabpanel">
            <div className="flow-slide-copy">
              <span className="flow-slide-eyebrow">{flow.eyebrow}</span>
              <h3>{flow.title}</h3>
              <p>{flow.description}</p>
              <small>{flow.proof}</small>
            </div>
            <FlowVisual step={activeFlow} />
          </div>
        </div>

        <div className="flow-relief-line">
          <span aria-hidden="true">✦</span>
          <p><strong>You invite the people.</strong> Gathered handles the prompts, organization, follow-up, first assembly, and service-ready files.</p>
        </div>
      </section>

      <section className="section product-tour-section" id="product-tour">
        <div className="section-kicker">The real Gathered experience</div>
        <div className="section-heading split-heading product-tour-heading">
          <h2>One calm place.<br />From first memory to final tribute.</h2>
          <p>
            This is the working product—not a mood board. Every screen is
            designed to lower the emotional and practical burden on a family.
          </p>
        </div>

        <div className="tour-tabs" role="tablist" aria-label="Explore the Gathered product">
          {(Object.keys(productTour) as TourKey[]).map((key) => (
            <button
              key={key}
              role="tab"
              aria-selected={activeTour === key}
              className={activeTour === key ? "active" : ""}
              onClick={() => setActiveTour(key)}
            >
              <span>{productTour[key].index}</span>
              {productTour[key].label}
            </button>
          ))}
        </div>

        <div className="tour-stage" key={activeTour} role="tabpanel">
          <div className="tour-copy">
            <span className="tour-eyebrow">{tour.eyebrow}</span>
            <h3>{tour.title}</h3>
            <p>{tour.description}</p>
            <div className="tour-points">
              {tour.bullets.map((bullet) => <Check key={bullet}>{bullet}</Check>)}
            </div>
            <div className="tour-note">
              <span aria-hidden="true">✦</span>
              Demo family and sample memories shown. No private family content.
            </div>
          </div>
          <div className={`tour-visual ${activeTour === "gather" ? "tour-mobile" : ""}`}>
            <div className="tour-window-bar">
              <span><i /><i /><i /></span>
              <small>Private · family.gathered.com</small>
            </div>
            <div className="tour-screen">
              <TourPreview mode={activeTour} />
            </div>
          </div>
        </div>

        <div className="tour-output-pair" aria-label="Examples of cinematic and print outputs">
          <figure>
            <div className="live-cinema-output"><img src="/images/demo-archive/1980s-lake-v1.webp" alt="Eleanor and her family beside the lake in a fictional memorial film frame" /><div><small>IN LOVING MEMORY</small><strong>Eleanor James</strong><span>1951 — 2026</span></div><i>▶</i></div>
            <figcaption><span>Service presentation</span><strong>Cinematic, graceful, offline-ready.</strong></figcaption>
          </figure>
          <figure>
            <div className="live-print-output"><div className="mini-display-board"><div><img src="/images/demo-archive/1950s-graduation-v1.webp" alt="Eleanor on graduation day" /><span>EARLY YEARS</span></div><div><small>CELEBRATING</small><strong>Eleanor James</strong><img src="/images/rose-portrait-v2.webp" alt="Eleanor" /><span>1951 — 2026</span></div><div><div><img src="/images/demo-archive/1970s-birthday-v1.webp" alt="A family birthday" /><img src="/images/demo-archive/2010s-coast-v1.webp" alt="A coastal family walk" /></div><span>A LIFE WELL LOVED</span></div></div><div className="mini-folded-program"><div><small>IN LOVING MEMORY</small><strong>Eleanor<br />James</strong><img src="/images/rose-portrait-v2.webp" alt="Eleanor" /><span>1951 — 2026</span></div><i /></div></div>
            <figcaption><span>Print package</span><strong>Pixel-perfect and ready for the counter.</strong></figcaption>
          </figure>
        </div>
      </section>

      <section className="section process-section" id="how">
        <div className="section-kicker">How Gathered works</div>
        <div className="section-heading split-heading">
          <h2>Three simple steps.<br />We carry the rest.</h2>
          <p>
            You should not have to become a producer, writer, designer, and
            family historian in the middle of everything else.
          </p>
        </div>

        <article className="process-row">
          <div className="process-copy">
            <span className="step-number">01</span>
            <p className="step-label">Start with what you know</p>
            <h3>Create the tribute in two minutes.</h3>
            <p>
              Tell us who you’re honoring, when the service is, and how you want
              it to feel. You do not need to arrive with an organized archive.
            </p>
            <div className="micro-proof">
              <span aria-hidden="true">✦</span>
              We create the plan from there.
            </div>
          </div>
          <div className="product-window setup-window">
            <div className="window-top">
              <div className="tiny-brand"><BrandMark /> Gathered</div>
              <span>Step 1 of 3</span>
            </div>
            <div className="setup-content">
              <p className="ui-kicker">Let’s begin gently</p>
              <h4>Who are we honoring?</h4>
              <div className="field">
                <small>Their name</small>
                <strong>Eleanor James</strong>
              </div>
              <div className="field-row">
                <div className="field">
                  <small>Service date</small>
                  <strong>August 8</strong>
                </div>
                <div className="field">
                  <small>Time available</small>
                  <strong>6 days</strong>
                </div>
              </div>
              <small className="field-prompt">How should it feel?</small>
              <div className="tone-chips">
                <span className="selected">Warm &amp; uplifting</span>
                <span>Reflective</span>
                <span>Faith-centered</span>
              </div>
              <div className="ui-button">Create Eleanor’s tribute →</div>
            </div>
          </div>
        </article>

        <article className="process-row reverse">
          <div className="process-copy">
            <span className="step-number">02</span>
            <p className="step-label">Share one private link</p>
            <h3>Everyone contributes a small piece.</h3>
            <p>
              Each person gets thoughtful prompts based on how they knew
              Eleanor. They can speak, type, upload photographs, or record a
              short video—no account and no app.
            </p>
            <div className="micro-proof">
              <span aria-hidden="true">✦</span>
              Most people finish in under three minutes.
            </div>
          </div>
          <div className="contribution-stage">
            <div className="phone-shell">
              <div className="phone-top"><span>9:41</span><span>● ●●</span></div>
              <div className="phone-photo">
                <img src="/images/rose-portrait-v2.webp" alt="Eleanor smiling in a garden" loading="lazy" decoding="async" />
                <div className="phone-photo-overlay">
                  <small>Remembering</small>
                  <strong>Eleanor</strong>
                </div>
              </div>
              <div className="phone-content">
                <small className="ui-kicker">A question just for you</small>
                <h4>What did Eleanor teach you without ever saying it directly?</h4>
                <div className="voice-recorder">
                  <button aria-label="Preview recording a voice memory">●</button>
                  <div className="waveform" aria-hidden="true">
                    {[9, 17, 25, 12, 31, 20, 10, 28, 36, 15, 23, 8, 19, 30].map(
                      (height, index) => (
                        <span key={index} style={{ height }} />
                      ),
                    )}
                  </div>
                  <small>0:42</small>
                </div>
                <div className="add-photo-row">
                  <span>＋</span>
                  Add a photograph to this memory
                </div>
              </div>
            </div>
            <div className="relationship-card">
              <small>Prompts adapt to each person</small>
              <strong>How did you know Eleanor?</strong>
              <div>
                <span>Sister</span><span className="active">Childhood friend</span>
                <span>Coworker</span><span>Grandchild</span>
              </div>
            </div>
          </div>
        </article>

        <article className="process-row">
          <div className="process-copy">
            <span className="step-number">03</span>
            <p className="step-label">Review, don’t assemble</p>
            <h3>We find the story—and what’s missing.</h3>
            <p>
              Gathered sorts every contribution into a life story, notices
              missing chapters, checks names and dates, and asks the right
              people for exactly what would make it complete.
            </p>
            <div className="micro-proof">
              <span aria-hidden="true">✦</span>
              You approve the facts, tone, and final tribute.
            </div>
          </div>
          <div className="product-window coverage-window">
            <div className="window-top">
              <div>
                <small>Eleanor’s tribute</small>
                <strong>Story coverage</strong>
              </div>
              <span className="secure-pill">Private</span>
            </div>
            <div className="coverage-summary">
              <div className="large-score">84<span>%</span></div>
              <div>
                <strong>A beautiful story is taking shape.</strong>
                <p>We found two small gaps worth filling.</p>
              </div>
            </div>
            <div className="life-bars">
              {[
                ["Early years", 48, "needs a voice"],
                ["Young adulthood", 76, "2 memories"],
                ["Family & home", 100, "complete"],
                ["Work & impact", 91, "complete"],
                ["Traditions & joy", 88, "complete"],
              ].map(([label, width, status]) => (
                <div className="life-bar" key={label}>
                  <span>{label}</span>
                  <div><i style={{ width: `${width}%` }} /></div>
                  <small>{status}</small>
                </div>
              ))}
            </div>
            <div className="smart-nudge">
              <span className="nudge-spark" aria-hidden="true">✦</span>
              <div>
                <small>A thoughtful next ask</small>
                <strong>Clara may have stories from Eleanor’s college years.</strong>
              </div>
              <button>Ask Clara</button>
            </div>
          </div>
        </article>
      </section>

      <section className="outputs-section" id="outputs">
        <div className="outputs-intro">
          <div className="section-kicker light">One story, beautifully complete</div>
          <h2>Professionally produced.<br />Never merely generated.</h2>
          <p>
            AI organizes the raw material and prepares the first cut. A rigorous,
            family-controlled production process turns it into one coherent film,
            signature poster, memorial display, large program, and letter-size handout worthy of the person.
          </p>
        </div>

        <div className="output-constellation">
          <div className="output-card film-card">
            <div className="film-image">
              <img src="/images/demo-archive/1990s-dance-v1.webp" alt="" loading="lazy" decoding="async" />
              <div className="play-button" aria-hidden="true">▶</div>
              <div className="film-title">
                <small>Chapter four</small>
                <strong>The door was always open.</strong>
              </div>
            </div>
            <div className="output-card-label"><span>01</span> Memorial film · 08:42</div>
          </div>
          <div className="output-card program-card">
            <div className="program-booklet-scene">
              <div className="program-inside-sheet"><div><small>ORDER OF SERVICE</small><b>Welcome &amp; opening words</b><b>Family remembrances</b><b>Photo tribute</b><b>Closing blessing</b></div><div><small>HER STORY</small><p>She made every arrival feel expected and every goodbye feel temporary.</p><strong>Thank you for carrying Eleanor’s stories forward.</strong></div></div>
              <div className="program-folded-cover"><small>IN LOVING MEMORY OF</small><h3>Eleanor<br />James</h3><img src="/images/rose-portrait-v2.webp" alt="Eleanor smiling" loading="lazy" decoding="async" /><span>1951 — 2026</span><i>A life gathered with love</i></div>
            </div>
            <div className="program-spec">11 × 8.5 in · two-sided · 5.5 × 8.5 finished</div>
            <div className="output-card-label"><span>02</span> Letter-size memorial handout</div>
          </div>
          <div className="output-card website-card">
            <div className="web-nav"><BrandMark /><span>Her story</span><span>Memories</span></div>
            <div className="web-photo">
              <img src="/images/rose-book-club-v2.webp" alt="" loading="lazy" decoding="async" />
            </div>
            <small>A LIFE GATHERED WITH LOVE</small>
            <h3>She made room<br />for everyone.</h3>
            <div className="web-button">Explore Eleanor’s story</div>
            <div className="output-card-label"><span>03</span> Living tribute</div>
          </div>
          <div className="output-card poster-card">
            <div className="display-board-scene">
              <div className="display-board-shadow" />
              <div className="display-panel display-panel--left"><small>THE EARLY YEARS</small><img src="/images/demo-archive/1950s-graduation-v1.webp" alt="Eleanor on graduation day" /><div className="display-photo-row"><img src="/images/demo-archive/1950s-kitchen-v1.webp" alt="Sisters laughing in a kitchen" /><img src="/images/demo-archive/1960s-wedding-v1.webp" alt="Eleanor on her wedding day" /></div><p>“She found joy everywhere, then made sure you saw it too.”</p></div>
              <div className="display-panel display-panel--center"><small>CELEBRATING THE LIFE OF</small><h3>Eleanor<br />James</h3><span>1951 — 2026</span><img className="display-hero-photo" src="/images/rose-portrait-v2.webp" alt="Portrait of Eleanor" /><strong>She made room for everyone.</strong><div className="display-photo-strip"><img src="/images/demo-archive/1970s-birthday-v1.webp" alt="A family birthday" /><img src="/images/demo-archive/1980s-lake-v1.webp" alt="A family lake day" /><img src="/images/demo-archive/2000s-baking-v1.webp" alt="Baking with a grandchild" /></div></div>
              <div className="display-panel display-panel--right"><small>A LIFE WELL LOVED</small><div className="display-photo-grid"><img src="/images/demo-archive/1970s-garden-v1.webp" alt="A community garden" /><img src="/images/demo-archive/1990s-dance-v1.webp" alt="An anniversary dance" /><img src="/images/demo-archive/2010s-coast-v1.webp" alt="A family coastal walk" /><img src="/images/demo-archive/recent-book-club-v1.webp" alt="Friends at book club" /></div><p>Her open door · Sunday dinners · The garden · Her unmistakable laugh</p></div>
            </div>
            <div className="display-board-spec">48 × 36 in · 12 / 24 / 12 in rigid panels · print-ready</div>
            <div className="output-card-label"><span>04</span> Tabletop trifold display</div>
          </div>
          <div className="output-card book-card signature-poster-card">
            <div className="signature-poster-scene" aria-label="A framed 24 by 36 inch memorial poster">
              <div className="signature-poster-shadow" />
              <div className="signature-poster-sheet">
                <header><small>IN LOVING MEMORY</small><strong>Eleanor James</strong><span>1951 — 2026</span></header>
                <img className="signature-poster-portrait" src="/images/rose-portrait-v2.webp" alt="Portrait of Eleanor" loading="lazy" decoding="async" />
                <blockquote>“She made room for everyone.”</blockquote>
                <div><img src="/images/demo-archive/1950s-graduation-v1.webp" alt="Graduation memory" /><img src="/images/demo-archive/1960s-wedding-v1.webp" alt="Wedding memory" /><img src="/images/demo-archive/1980s-lake-v1.webp" alt="Family lake memory" /><img src="/images/demo-archive/recent-book-club-v1.webp" alt="Book club memory" /></div>
              </div>
            </div>
            <div className="signature-poster-copy"><small>THE ROOM-SCALE CENTERPIECE</small><h3>One portrait.<br />A whole life.</h3><p>Distance-view typography, a family-approved line, disciplined supporting photographs, exact focal crops, and measured print resolution.</p><span>24 × 36 in · bleed + trim · physical proof required</span></div>
            <div className="output-card-label"><span>05</span> Signature memorial poster</div>
          </div>
          <div className="output-card archive-card">
            <div className="archive-stack">
              <span>MP4</span><span>PDF</span><span>JPG</span>
            </div>
            <h3>Your family archive</h3>
            <p>Every original, organized and ready to download.</p>
            <div className="download-row">
              <span>↓</span>
              <div><strong>Gathered_Eleanor.zip</strong><small>Full-resolution · 2.8 GB</small></div>
            </div>
            <div className="output-card-label"><span>06</span> Complete archive</div>
          </div>
        </div>

        <div className="output-explorer">
          <div className="output-tabs" role="tablist" aria-label="Explore tribute outputs">
            {(Object.keys(outputDetails) as OutputKey[]).map((key) => (
              <button
                key={key}
                role="tab"
                aria-selected={activeOutput === key}
                className={activeOutput === key ? "active" : ""}
                onClick={() => setActiveOutput(key)}
              >
                {outputDetails[key].label}
              </button>
            ))}
          </div>
          <div className="output-detail" role="tabpanel">
            <div>
              <span className="detail-index">Included</span>
              <h3>{active.title}</h3>
            </div>
            <div>
              <p>{active.description}</p>
              <small>{active.meta}</small>
            </div>
          </div>
        </div>

        <div className="production-standard" aria-label="Gathered production standard">
          <div className="production-standard__intro"><span>THE GATHERED PRODUCTION STANDARD</span><h3>Nothing leaves as an AI draft.</h3><p>A tribute is only complete after the story, images, sound, and physical outputs have each passed a human review and technical preflight.</p></div>
          <div className="production-standard__steps">
            <article><span>01</span><div><strong>Editorial integrity</strong><p>Every quotation and biographical statement stays linked to the person who shared it. Repetition is edited; facts are never invented.</p></div></article>
            <article><span>02</span><div><strong>Creative direction</strong><p>The family chooses one emotional lens. Typography, crops, pacing, transitions, music, web, and print follow the same art direction.</p></div></article>
            <article><span>03</span><div><strong>Family picture lock</strong><p>The organizer approves every included memory, sensitive detail, headline, photograph, crop, attribution, and chapter order.</p></div></article>
            <article><span>04</span><div><strong>Mastering and preflight</strong><p>Film timing, soundtrack rights, image resolution, print dimensions, folds, margins, and offline playback are checked before final delivery.</p></div></article>
          </div>
        </div>
      </section>

      <section className="section intelligence-section" id="intelligence">
        <div className="intelligence-copy">
          <div className="section-kicker">Quietly intelligent</div>
          <h2>Not a file drop.<br />A thoughtful producer.</h2>
          <p>
            Gathered understands that a complete life is more than the most
            recent photographs. It builds a balanced story across people,
            chapters, and decades—without inventing a word.
          </p>
          <div className="intelligence-points">
            <Check>Prompts tailored to each relationship</Check>
            <Check>Missing people and life chapters identified</Check>
            <Check>Duplicate photographs and stories gently resolved</Check>
            <Check>Every important fact traced to its source</Check>
          </div>
        </div>
        <div className="intelligence-visual">
          <div className="life-map">
            <div className="life-map-head">
              <div>
                <small>STORY MAP</small>
                <strong>Eleanor’s whole life</strong>
              </div>
              <span>27 contributions</span>
            </div>
            <div className="timeline-line">
              {[
                ["1951", "Childhood", 5],
                ["1971", "Coming of age", 3],
                ["1980", "Family", 8],
                ["1994", "Work & service", 6],
                ["2018", "Grandchildren", 5],
              ].map(([year, label, count], index) => (
                <div className="timeline-chapter" key={label}>
                  <span className={index === 1 ? "chapter-dot gap" : "chapter-dot"} />
                  <small>{year}</small>
                  <strong>{label}</strong>
                  <em>{count} memories</em>
                </div>
              ))}
            </div>
            <div className="context-card">
              <span aria-hidden="true">✦</span>
              <div>
                <small>We noticed something</small>
                <strong>
                  There are wonderful stories about Eleanor as a mother, but
                  little about who she was before 1970.
                </strong>
                <p>We prepared two gentle questions for her sister and oldest friend.</p>
              </div>
            </div>
          </div>
          <div className="prompt-fan">
            <div className="prompt-card card-back">
              <small>FOR A COWORKER</small>
              <strong>What did people learn by watching Eleanor work?</strong>
            </div>
            <div className="prompt-card card-middle">
              <small>FOR A GRANDCHILD</small>
              <strong>What small tradition of hers will you keep?</strong>
            </div>
            <div className="prompt-card card-front">
              <small>FOR HER SISTER</small>
              <strong>What was Eleanor like before the rest of us knew her?</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="section story-section">
        <div className="archive-image">
          <img
            src="/images/demo-archive/contact-family-v1.webp"
            alt="A warm collection of photographs from different chapters of Eleanor's life"
            loading="lazy"
            decoding="async"
          />
          <div className="archive-note">
            <span>27</span>
            voices shaped this story
          </div>
        </div>
        <div className="story-copy">
          <div className="section-kicker">From fragments to a whole life</div>
          <blockquote>
            “She made room for everyone—at her table, in her garden, and in her
            plans.”
          </blockquote>
          <p>
            One friend remembered the cross-country drive. Her sister found the
            college photograph. A grandchild recorded the sound of her laugh.
            Gathered recognized the thread running through all of it and built
            the story around what the family actually shared.
          </p>
          <div className="source-line">
            <span className="source-avatars"><i>CR</i><i>MJ</i><i>AK</i></span>
            <span>Every sentence remains connected to a real person and memory.</span>
          </div>
        </div>
      </section>

      <section className="service-ready">
        <div className="service-ready-copy">
          <div className="section-kicker light">Ready when the room is</div>
          <h2>Beautiful is the promise.<br />Preflight makes it dependable.</h2>
          <p>
            Everything is prepared for the real conditions of the day—from the
            lobby easel to the television that may not have Wi‑Fi.
          </p>
        </div>
        <div className="readiness-grid">
          <article>
            <span>01</span>
            <strong>Reviewed by you</strong>
            <p>Names, dates, sensitive details, and final tone require family approval.</p>
          </article>
          <article>
            <span>02</span>
            <strong>Works offline</strong>
            <p>Downloadable MP4 and presentation files are tested before the service.</p>
          </article>
          <article>
            <span>03</span>
            <strong>Physical-output proof</strong>
            <p>Board dimensions, folds, margins, image resolution, and printer instructions are verified before final export.</p>
          </article>
          <article>
            <span>04</span>
            <strong>Real voices only</strong>
            <p>No synthetic impersonations. Their people tell the story in their own words.</p>
          </article>
        </div>
      </section>

      <section className="section stewardship-section" id="privacy">
        <div className="stewardship-heading">
          <div className="section-kicker">Stewardship, not storage</div>
          <h2>Irreplaceable memories deserve more than “upload complete.”</h2>
          <p>
            A successful submission is protected as an original, a story, and
            a recoverable record. The family stays in control of who can see it,
            what can be published, and when everything is taken home.
          </p>
        </div>
        <div className="stewardship-grid">
          <article>
            <span>01</span>
            <div>
              <strong>Originals stay original</strong>
              <p>Enhancements, crops, and print versions are always new files. Nothing overwrites the source.</p>
            </div>
          </article>
          <article>
            <span>02</span>
            <div>
              <strong>A receipt means it is safe</strong>
              <p>Confirmation appears only after the media and its story are securely recorded together.</p>
            </div>
          </article>
          <article>
            <span>03</span>
            <div>
              <strong>Private, expiring access</strong>
              <p>Media is never left at a public address. Organizer access and contributor contact details stay locked down.</p>
            </div>
          </article>
          <article>
            <span>04</span>
            <div>
              <strong>Your complete archive</strong>
              <p>Download full-resolution originals, approved stories, and structured records at any time.</p>
            </div>
          </article>
        </div>
        <div className="stewardship-footer">
          <span className="stewardship-lock" aria-hidden="true">⌁</span>
          <p><strong>Private by default.</strong> Nothing is indexed, printed, or published until an organizer approves it.</p>
          <a href="#faq">Read the gentle answers <span aria-hidden="true">→</span></a>
        </div>
      </section>

      <section className="section for-organizer">
        <div>
          <div className="section-kicker">Built for the person everyone turns to</div>
          <h2>You can check this off the list.</h2>
        </div>
        <div className="organizer-list">
          <div><span>Today</span><strong>Create the private tribute space</strong><em>2 minutes</em></div>
          <div><span>Next</span><strong>Send one link to family and friends</strong><em>We guide them</em></div>
          <div><span>Then</span><strong>Approve the story as it comes together</strong><em>No editing timeline</em></div>
          <div className="complete"><span>Done</span><strong>Download everything the service needs</strong><em>Ready</em></div>
        </div>
      </section>

      <section className="pricing-section" id="pricing">
        <div className="pricing-intro">
          <div className="section-kicker">Simple, one-time family packages</div>
          <h2>Choose only the help your family needs.</h2>
          <p>
            No subscription. No surprise rendering fees. Every package includes
            private collection, family review, English and Spanish, and a complete archive.
          </p>
        </div>
        <div className="pricing-grid">
          <article className="price-card">
            <div className="price-card-top">
              <span className="price-name">Gather</span>
              <p>Collect the memories beautifully and keep every original.</p>
            </div>
            <div className="price"><sup>$</sup>149 <small>one time</small></div>
            <div className="price-rule" />
            <div className="price-features">
              <Check>Private contribution link for 60 days</Check>
              <Check>Photo, story, voice, and video prompts</Check>
              <Check>Organizer review and moderation</Check>
              <Check>Full-resolution family archive</Check>
            </div>
            <a className="price-button" href={signupUrl("gather")}>Choose Gather <span aria-hidden="true">→</span></a>
          </article>

          <article className="price-card featured">
            <div className="popular-label">Most chosen for a service</div>
            <div className="price-card-top">
              <span className="price-name">Ceremony</span>
              <p>Everything needed for a beautiful, dependable memorial service.</p>
            </div>
            <div className="price"><sup>$</sup>399 <small>one time</small></div>
            <div className="price-rule" />
            <div className="price-features">
              <Check>Everything in Gather</Check>
              <Check>Cinematic service slideshow</Check>
              <Check>Memorial website and QR code</Check>
              <Check>Program, trifold, poster, and print proof</Check>
              <Check>Offline ceremony files and rehearsal check</Check>
            </div>
            <a className="price-button" href={signupUrl("ceremony")}>Choose Ceremony <span aria-hidden="true">→</span></a>
          </article>

          <article className="price-card">
            <div className="price-card-top">
              <span className="price-name">Heirloom</span>
              <p>A produced family film and keepsake collection built to last.</p>
            </div>
            <div className="price"><sup>$</sup>899 <small>one time</small></div>
            <div className="price-rule" />
            <div className="price-features">
              <Check>Everything in Ceremony</Check>
              <Check>Produced 5–10 minute memorial film</Check>
              <Check>Family voice memories woven into the story</Check>
              <Check>Restoration for up to 30 photographs</Check>
              <Check>Print-ready keepsake book</Check>
            </div>
            <a className="price-button" href={signupUrl("heirloom")}>Choose Heirloom <span aria-hidden="true">→</span></a>
          </article>
        </div>
        <p className="pricing-note">
          Need something different? Begin with Gather and add only the pieces that matter.
          Tax and physical printing are shown separately before approval.
        </p>
      </section>

      <section className="partner-section" id="partners">
        <div className="partner-copy">
          <div className="section-kicker light">For funeral homes, celebrants, and care teams</div>
          <h2>Give every family a finished tribute—not another folder of files.</h2>
          <p>
            Offer Gathered as a thoughtful extension of your care. Families get
            a calm, branded collection experience; your team gets a clear view
            of readiness without becoming the production department.
          </p>
          <div className="partner-points">
            <Check>Co-branded invitations and family experience</Check>
            <Check>One workspace across every active service</Check>
            <Check>Self-service, staff-assisted, or fully produced</Check>
            <Check>Predictable volume pricing and no long contract</Check>
          </div>
          <a className="button partner-button" href="mailto:hello@gatheredwithlove.com?subject=Gathered%20partner%20program">Explore the partner model <span aria-hidden="true">→</span></a>
        </div>
        <div className="partner-dashboard" aria-label="Professional partner workspace preview">
          <div className="partner-dashboard-head">
            <div><BrandMark /><strong>Gathered for North &amp; Pine</strong></div>
            <span>4 active families</span>
          </div>
          <div className="partner-summary">
            <article><span>Services this week</span><strong>04</strong><small>All on track</small></article>
            <article><span>Families collecting</span><strong>03</strong><small>31 new memories</small></article>
            <article><span>Ready to deliver</span><strong>02</strong><small>Files proofed</small></article>
          </div>
          <div className="partner-list">
            <div className="partner-list-head"><span>Family</span><span>Next need</span><span>Readiness</span></div>
            <div><strong>Eleanor James</strong><span>Approve program</span><em>92%</em></div>
            <div><strong>Robert Chen</strong><span>2 more stories</span><em>74%</em></div>
            <div><strong>Marisol Vega</strong><span>Rehearsal check</span><em>100%</em></div>
          </div>
          <div className="partner-care-note"><span>✦</span><p><strong>A calm handoff.</strong> We guide the family and keep your team informed.</p></div>
        </div>
      </section>

      <section className="section faq-section" id="faq">
        <div className="faq-heading">
          <div className="section-kicker">A few gentle answers</div>
          <h2>You’re not expected to know how to do this.</h2>
        </div>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <article className={openFaq === index ? "open" : ""} key={faq.question}>
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                aria-expanded={openFaq === index}
              >
                <span>{faq.question}</span>
                <i aria-hidden="true">{openFaq === index ? "−" : "+"}</i>
              </button>
              <div className="faq-answer"><p>{faq.answer}</p></div>
            </article>
          ))}
        </div>
      </section>

      <section className="final-cta" id="start">
        <div className="final-cta-photo">
          <img src="/images/rose-family-memory-v2.webp" alt="" loading="lazy" decoding="async" />
        </div>
        <div className="final-cta-content">
          <BrandMark />
          <div className="section-kicker light">We’ll help you get it right</div>
          <h2>Begin with a name.<br />We’ll guide the rest.</h2>
          <p>
            Choose a family package, or see how Gathered can become part of the
            care your organization already provides.
          </p>
          <div className="final-actions">
            <a className="button button-light" href={appSignupUrl}>
              Create a memorial <span aria-hidden="true">→</span>
            </a>
            <a className="final-text-link" href="#partners">For professionals</a>
          </div>
          <small>One-time family packages · Private by default · You approve everything</small>
        </div>
      </section>

      <footer>
        <a className="brand footer-brand" href="#top">
          <BrandMark />
          <span>Gathered</span>
        </a>
        <p>Beautifully gathered for someone who matters.</p>
        <div><a href="#product-tour">Product</a><a href="#pricing">Packages</a><a href="#partners">Partners</a><a href="#top">Back to top ↑</a></div>
      </footer>
    </main>
  );
}
